import { useEffect, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import {
  adminGetOperatorRunQuery,
  adminListOperatorRunChatsQuery,
  adminOperatorRunHealthQuery
} from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import SearchIcon from "@mui/icons-material/Search"
import {
  Alert,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient
} from "@tanstack/react-query"
import { query } from "@utils/graphql"
import {
  CustomerPerson,
  CustomerPersonsListResponse,
  QueryCustomerPersonsArgs
} from "__generated__/graphql"
import customerPersonsQuery from "__generated__/queries/queries/customerPersons"

import OperatorChatList from "./sub_components/OperatorChatList"
import OperatorChatView from "./sub_components/OperatorChatView"
import OperatorFilterPopover from "./sub_components/OperatorFilterPopover"
import OperatorRunDetail from "./sub_components/OperatorRunDetail"
import OperatorSummary from "./sub_components/OperatorSummary"
import {
  OperatorPeriod,
  OperatorShow,
  periodFrom,
  showFilter
} from "./sub_components/operatorRun.utils"
import useOperatorRunsRealtime from "./useOperatorRunsRealtime"

const PAGE_SIZE = 30
const SEARCH_DEBOUNCE_MS = 400
const CHAT_PARAM = "chat"

/**
 * AI Hub → Operator Logs, one row per Operator chat like AI Logs: the chat's messages with the run
 * behind each answer; a run opens its model/tool steps (AiOperatorRunSteps) over the chat.
 */
export default function AiOperatorPage() {
  const { translate } = useTranslate()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { runId } = useParams<{ runId?: string }>()
  const [searchParams] = useSearchParams()
  const { control } = useForm<{ customerId: string }>({
    defaultValues: { customerId: "" }
  })
  const customerId = useWatch({ control, name: "customerId" })
  const [cPersonId, setCPersonId] = useState("")
  const [show, setShow] = useState<OperatorShow>("")
  const [period, setPeriod] = useState<OperatorPeriod>("24h")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")

  useEffect(() => {
    const timer = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => setCPersonId(""), [customerId])

  useOperatorRunsRealtime({ customerId, cPersonId })

  // A shared run link (/ai/operator/:runId) carries no chat; the run's own record names it.
  const linkedRun = useQuery({
    queryKey: ["ai-operator-run", runId],
    enabled: !!runId && !searchParams.get(CHAT_PARAM),
    queryFn: async () => {
      const response = await adminGetOperatorRunQuery(runId as string)
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || "failed")
      }
      return response.data
    },
    retry: false
  })
  const chatId =
    searchParams.get(CHAT_PARAM) ?? linkedRun.data?.run.chatId ?? undefined

  const go = (next: { chatId?: string; runId?: string }) => {
    const path = next.runId ? `/ai/operator/${next.runId}` : "/ai/operator"
    navigate(next.chatId ? `${path}?${CHAT_PARAM}=${next.chatId}` : path, {
      replace: true
    })
  }

  const persons = useQuery({
    queryKey: ["ai-operator-persons", customerId],
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await query<
        { customerPersons?: CustomerPersonsListResponse },
        QueryCustomerPersonsArgs
      >({
        query: customerPersonsQuery,
        variables: {
          customerId,
          pageSize: 200,
          page: 1,
          excludeAi: true,
          notSelectBot: true
        }
      })
      const docs: (CustomerPerson | null)[] =
        response.data?.customerPersons?.data?.docs ?? []
      return docs.filter((person): person is CustomerPerson => !!person)
    }
  })

  const chats = useInfiniteQuery({
    queryKey: [
      "ai-operator-runs",
      "chats",
      customerId,
      cPersonId,
      show,
      period,
      search
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const from = periodFrom(period)
      const response = await adminListOperatorRunChatsQuery({
        page: pageParam,
        limit: PAGE_SIZE,
        ...(customerId ? { customerId } : {}),
        ...(cPersonId ? { cPersonId } : {}),
        ...showFilter(show),
        ...(search ? { search } : {}),
        ...(from ? { from } : {})
      })
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || "failed")
      }
      return response.data
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.hasMore ? pages.length + 1 : undefined
  })
  const items = chats.data?.pages.flatMap((page) => page.items) ?? []

  const health = useQuery({
    queryKey: ["ai-operator-health", customerId, cPersonId, period],
    queryFn: async () => {
      const response = await adminOperatorRunHealthQuery({
        from: periodFrom(period, Date.now(), true) as string,
        ...(customerId ? { customerId } : {}),
        ...(cPersonId ? { cPersonId } : {})
      })
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || "failed")
      }
      return response.data
    }
  })

  return (
    <Box sx={{ height: "100%", display: "flex", overflow: "hidden" }}>
      <Stack
        sx={{
          width: 320,
          flexShrink: 0,
          borderRight: 1,
          borderColor: "divider"
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.5} p={1}>
          <OperatorFilterPopover
            control={control}
            customerId={customerId}
            persons={persons.data ?? []}
            cPersonId={cPersonId}
            onPerson={setCPersonId}
            period={period}
            onPeriod={setPeriod}
            show={show}
            onShow={setShow}
          />
          <Tooltip title={translate("ai_operator_refresh")}>
            <IconButton
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: ["ai-operator-runs"]
                })
                void queryClient.invalidateQueries({
                  queryKey: ["ai-operator-health"]
                })
                void queryClient.invalidateQueries({
                  queryKey: ["ai-operator-chat"]
                })
                void queryClient.invalidateQueries({
                  queryKey: ["ai-operator-run"]
                })
              }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            fullWidth
            size="small"
            placeholder={translate("ai_operator_search")}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              sx: { borderRadius: 2 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Stack>
        <Divider />
        {chats.error ? (
          <Alert severity="error">{chats.error.message}</Alert>
        ) : null}
        <OperatorChatList
          items={items}
          selectedChatId={chatId}
          loading={chats.isFetching && !chats.isFetchingNextPage}
          hasMore={!!chats.hasNextPage}
          loadingMore={chats.isFetchingNextPage}
          onSelect={(id) => go({ chatId: id })}
          onLoadMore={() => void chats.fetchNextPage()}
        />
      </Stack>

      <Box sx={{ flex: 1, minWidth: 0, position: "relative", display: "flex" }}>
        {chatId ? (
          <OperatorChatView
            key={chatId}
            chatId={chatId}
            activeRunId={runId}
            onOpenRun={(id) => go({ chatId, runId: id })}
          />
        ) : (
          <Stack gap={2} p={3} flex={1}>
            <Typography variant="h6">
              {translate("ai_operator_title")}
            </Typography>
            <OperatorSummary
              health={health.data}
              onOpenRun={(id) => go({ runId: id })}
            />
            <Typography variant="body2" color="text.secondary">
              {translate("ai_operator_pick_run")}
            </Typography>
          </Stack>
        )}
        {runId ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 4,
              bgcolor: "background.paper",
              boxShadow: 3
            }}
          >
            <OperatorRunDetail
              key={runId}
              runId={runId}
              onClose={() => go({ chatId })}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
